"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, Factory, ClipboardCheck, ShieldCheck, HardHat, FileCheck2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accessContexts = [{
  id: "commercial",
  title: "Town-centre commercial",
  tag: "High Street & Retail",
  icon: Building2,
  description: "Pedestrian gantries, pavement license compliance, and out-of-hours erection to keep town-centre retail and offices operating safely.",
  points: ["Public protection and debris netting", "Pavement license management", "Out-of-hours delivery and dismantling"]
}, {
  id: "residential",
  title: "Residential terraces",
  tag: "Steep Slopes & Narrow Plots",
  icon: Home,
  description: "Tailored structures for Wycombe's steep hillsides, tight side-access alleyways, roof renewals, and chimney access on terraced homes.",
  points: ["Narrow-access and rear-garden solutions", "Chimney saddles and edge protection", "Careful ground and property protection"]
}, {
  id: "industrial",
  title: "Industrial estates",
  tag: "Cressex & Sands Areas",
  icon: Factory,
  description: "High-level access, internal birdcages, and edge protection for warehouses, logistics units, and industrial facilities across local estates.",
  points: ["Internal birdcage and crash decks", "Solar installer roof access", "Clear access lanes for daily operations"]
}];
const safetySteps = [{
  step: "01",
  title: "Site assessment",
  icon: HardHat,
  description: "We inspect ground conditions, boundary constraints, overhead services, and public interfaces before scaffolding arrives on site."
}, {
  step: "02",
  title: "Safe access erection",
  icon: ShieldCheck,
  description: "Qualified scaffolders build strictly to TG20 guidance and project specifications with full collective fall protection."
}, {
  step: "03",
  title: "Handover inspection",
  icon: FileCheck2,
  description: "A formal handover certificate and Scafftag are issued once the structure passes rigorous structural and safety checks."
}, {
  step: "04",
  title: "Regular inspections",
  icon: ClipboardCheck,
  description: "Mandatory statutory 7-day re-inspections and post-adverse-weather audits keep your site compliant throughout the build."
}];
export default function WycombeSafety() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  return <section id="highwycombesafetyandaccess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="max-w-3xl mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/5">
            High Wycombe Access & Safety
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Scaffolding built for High Wycombe&apos;s terrain and streetscapes
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From compact town-centre frontages and terraced valley roads to high-bay industrial units, we design, build, and inspect safe access across the area.
          </p>
        </motion.div>

        {/* High Wycombe Access Contexts Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {accessContexts.map((context, i) => {
          const Icon = context.icon;
          return <motion.div key={context.id} data-index={i} variants={fadeUp} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                        <Icon className="size-6" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {context.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {context.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {context.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <ul className="space-y-2.5">
                      {context.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Safety & Handover Process */}
        <div className="rounded-2xl border border-border bg-muted/40 p-8 sm:p-10 lg:p-12 mb-16">
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="max-w-2xl mb-12">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Safety process and handover standards
            </h3>
            <p className="mt-2 text-muted-foreground">
              Every scaffold is planned, erected, and handed over under documented health and safety procedures.
            </p>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {safetySteps.map((step, i) => {
            const StepIcon = step.icon;
            return <motion.div key={step.step} data-index={i} variants={fadeUp} className="bg-card rounded-xl p-6 border border-border shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                        {step.step}
                      </span>
                      <StepIcon className="size-5 text-muted-foreground" />
                    </div>
                    <h4 className="text-base font-bold text-foreground mb-2">
                      {step.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Accreditations & CTA Banner */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="rounded-2xl border border-border bg-card p-8 sm:p-10 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="space-y-4 max-w-2xl text-center lg:text-left">
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <Badge variant="default" className="bg-primary text-primary-foreground font-semibold px-3 py-1">
                NASC Member
              </Badge>
              <Badge variant="secondary" className="font-semibold px-3 py-1">
                CHAS Accredited
              </Badge>
            </div>
            <h3 className="text-xl sm:text-2xl font-default font-bold text-card-foreground">
              Confirmed safety accreditations on every project
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              BMF Scaffolding operates under full NASC and CHAS compliance. We provide complete risk assessments, method statements, and inspection certificates for residential and commercial works.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0 justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                <span>Request a quote</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/HealthSafetyAccreditationsPage">
                <span>View accreditations</span>
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}